/** @jsxImportSource @emotion/react */
'use memo'

import { LoadingOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import React from 'react'
import Text from 'src/components/ts-components/Text'
import { font } from 'src/components/css'
import { renderIconWithColor } from 'src/components/ts-components/Button/renderIconWithColor'

type Size = 'small' | 'default'

type HeaderActionButtonProps = {
  text: string
  /** Omitted on a menu trigger: the Dropdown wrapped around it handles the click. */
  onClick?: () => void
  disabled?: boolean
  isLoading?: boolean
  /**
   * `outlined` is `secondary` with the tenant accent for its border: an action
   * that leads to more choices (Import Players From Previous Seasons), where
   * `primary` would read as the dialog's confirm.
   */
  variant: 'primary' | 'secondary' | 'outlined'
  /**
   * Spans its container — a dialog's one call to action above a form. The
   * label wraps and the button grows on a phone, rather than pushing the
   * dialog past the screen's edge.
   */
  fullWidth?: boolean
  icon?: React.ReactNode
  /** Optional icon AFTER the label — e.g. a dropdown caret on a menu trigger. */
  trailingIcon?: React.ReactNode
  textWeight?: 'regular' | 'medium' | 'bold' | 'semiBold'
  size?: Size
  /** Optional test hook, so callers can drop antd Button without losing theirs. */
  'data-testid'?: string
}

const sizeMap: Record<
  Size,
  { height: number; padding: string; minWidth: number }
> = {
  default: { height: 40, padding: '8px 16px', minWidth: 120 },
  small: { height: 32, padding: '4px 12px', minWidth: 0 },
}

const HeaderActionButton = ({
  text,
  onClick,
  disabled = false,
  isLoading = false,
  variant,
  icon,
  trailingIcon,
  textWeight = 'regular',
  size = 'default',
  fullWidth = false,
  'data-testid': testId,
}: HeaderActionButtonProps) => {
  const isPrimary = variant === 'primary'
  const isOutlined = variant === 'outlined'
  const isDisabled = disabled || isLoading
  const isSmall = size === 'small'
  const dims = sizeMap[size]
  const contentColor = isDisabled
    ? colors.NEUTRAL_50
    : isPrimary
    ? PRIMARY_BUTTON_CONTENT.text
    : colors.NEUTRAL_0

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={isDisabled}
      data-testid={testId}
      css={css`
        /* A native <button> does not inherit font-family. The label below sets
           its own via the paragraph preset, but without this any bare text child
           silently falls back to the browser default (Arial). */
        ${font.body}
        display: inline-flex;
        min-width: ${dims.minWidth}px;
        height: ${dims.height}px;
        padding: ${dims.padding};
        ${fullWidth &&
        `
          /* One line at the usual height; on a phone the label wraps and the
             button grows with it. */
          width: 100%;
          height: auto;
          min-height: ${dims.height}px;
          padding-block: 6px;
        `}
        justify-content: center;
        align-items: center;
        gap: ${isSmall ? 6 : 8}px;
        border-radius: 8px;
        border: 1px solid
          ${isDisabled
            ? colors.SECONDARY_200
            : isPrimary || isOutlined
            ? colors.PRIMARY
            : colors.NEUTRAL_50};
        background: ${isDisabled
          ? colors.SECONDARY_200
          : isPrimary
          ? colors.PRIMARY
          : 'transparent'};
        cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
        transition: background-color 0.2s ease, border-color 0.2s ease,
          opacity 0.2s ease;

        &:hover:not(:disabled) {
          background: ${isPrimary
            ? colors.PRIMARY_LIGHT
            : colors.SECONDARY_400};
          border-color: ${isPrimary || isOutlined
            ? colors.PRIMARY_LIGHT
            : colors.NEUTRAL_0};
        }
      `}
    >
      {isLoading ? (
        <LoadingOutlined
          css={css`
            color: ${contentColor};
            font-size: 16px;
          `}
        />
      ) : (
        <>
          {icon ? (
            <div
              css={css`
                display: inline-flex;
                align-items: center;
                color: ${contentColor};
              `}
            >
              {renderIconWithColor(icon, contentColor)}
            </div>
          ) : null}
          <Text
            variant={isSmall ? 'paragraphXSmall' : 'paragraphMedium'}
            weight={textWeight}
            color={contentColor}
            style={
              fullWidth
                ? 'margin: 0; text-align: center;'
                : 'margin: 0; white-space: nowrap;'
            }
          >
            {text}
          </Text>
          {trailingIcon ? <div>{trailingIcon}</div> : null}
        </>
      )}
    </button>
  )
}

export default HeaderActionButton
